import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  CircularProgress,
  TableFooter,
  TablePagination,
} from "@material-ui/core";
import { AccessTime, PermIdentity, ChevronRight } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { isNativeCapacitorRuntime } from "../../services/revenueCatService";
import "./quiz-history-table.scss";
import mixpanel from 'mixpanel-browser';
import { scoreBand } from "../../utils/score-band";

// Collapse an exhaustive category list into a scannable summary: show up to
// MAX_TAGS values, then "+N more"; the full list stays available on hover
// (title). Without this, a quiz spanning every discipline dumps a ~15-item
// comma list into each cell, inflating every history row to ~190px of
// unreadable, identical text (UX-sweep P1). Empty lists render an em dash
// instead of a blank cell.
const MAX_TAGS = 2;
export const summarizeCats = (list) => {
  if (!Array.isArray(list) || list.length === 0) return { text: "—", title: "" };
  const full = list.join(", ");
  if (list.length <= MAX_TAGS) return { text: full, title: full };
  return { text: `${list.slice(0, MAX_TAGS).join(", ")} +${list.length - MAX_TAGS} more`, title: full };
};

// Display-only provenance for a curriculum-filtered quiz (product/CONTRACT-UI-SLICE2.md §9.5).
// Neutral wording: no score, percentage or readiness is derived from it.
export const curriculumProvenanceText = (row) => {
  const rows = row?.curriculumRowIds;
  if (!Array.isArray(rows) || rows.length === 0 || !row?.curriculumEdition) return null;
  return `Surgery topics: ${rows.length}`;
};

const CategoryCell = ({ list }) => {
  const { text, title } = summarizeCats(list);
  return (
    <TableCell className="cell-width hide-in-mobile" title={title}>
      {text}
    </TableCell>
  );
};

const QuizHistoryTable = (props) => {
  // viewOnly: a tutor viewing a linked student's history — hide the Review
  // action (drilling into a student's result needs its own guarded read) and
  // show a neutral empty state instead of the student's own "create a quiz" CTA.
  // onOversightOpen (contract qlearn-oversight-quiz-open-v1): when the
  // mentorship student panel provides it, viewOnly rows regain an Open action
  // that routes through the caller (the guarded oversight route) — NEVER the
  // owner-scoped /result/:id navigation below. The native card click follows
  // the same rule, closing the latent path where a card tap in a viewOnly
  // context navigated to the owner route.
  const { questions, isLoading, viewOnly, onOversightOpen } = props;
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const role = window.localStorage.getItem("role");
    setUserRole(role);
  }, []);

  const reviewQuestion = (item) => {
    if (onOversightOpen) {
      onOversightOpen(item);
      return;
    }
    if (viewOnly) {
      // viewOnly without an oversight handler: no navigation target exists —
      // the owner route would load the VIEWER's scope, not this student's.
      return;
    }
    // Analytics must never block navigation: with 'userdetails' missing or
    // corrupt (cleared storage, partial eviction, auth paths that don't set
    // it), the unguarded parse threw and the Review button silently did
    // NOTHING (UX-sweep slice-2 P1 — reproduced, click-verified).
    try {
      const userinfos = JSON.parse(localStorage.getItem('userdetails')) || {};
      mixpanel.track('QH-review', {
        $name: userinfos.name,
        "Value": 9.00, // Assign a monetary value to the event
        email: userinfos.email,
        role: userRole
      });
    } catch (e) {
      // tracking failure is not a navigation failure
    }
    history.push(`/result/${item.id}`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedQuestions = rowsPerPage > 0
    ? questions?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : questions;

  // Native app renders card rows instead of the desktop table (gap-spec
  // screen 06); web keeps the table below, pixel-identical.
  if (isNativeCapacitorRuntime()) {
    return (
      <Box component="div" m={1} className="quiz-history-table-container history-cards">
        <Typography variant="h4" className="sub-title">
          History
        </Typography>

        {(!questions || questions.length === 0) &&
          <Typography className="history-empty-state">
            No quizzes yet — quizzes you complete will show up here.
          </Typography>
        }

        {pagedQuestions?.map((row, index) => (
          <Box
            component="div"
            key={index}
            className="history-card"
            data-testid="history-card"
            onClick={() => reviewQuestion(row)}
          >
            <Box component="div" className="quiz-percent-progress history-card-ring">
              <CircularProgress
                variant="determinate"
                className="circular-progress-bkg"
                size={56}
                thickness={3}
                value={100}
              />
              <CircularProgress
                variant="determinate"
                size={56}
                thickness={3}
                value={row.percentageCorrect}
                className={
                  row.percentageCorrect >= 70 ? 'score-good'
                    : row.percentageCorrect >= 50 ? 'score-mid'
                      : 'score-low'
                }
              />
              <Box component="div" className="quiz-progress-text">
                <Box component="div" className="quiz-progress-text-num">
                  {row.percentageCorrect}%
                </Box>
              </Box>
            </Box>
            <Box component="div" className="history-card-main">
              <Typography className="history-card-title">{row.title}</Typography>
              <Typography className="history-card-meta">
                {row.total} questions · {moment(row.complete).format("MMM D, YYYY")}
              </Typography>
              <Typography className="history-card-meta">
                Timer {row.timerOn ? "on" : "off"}
                {userRole === 'ROLE_TUTOR' ? ` · Tutor ${row.tutorOn ? "on" : "off"}` : ''}
              </Typography>
              {curriculumProvenanceText(row) &&
                <Typography className="history-card-meta">{curriculumProvenanceText(row)}</Typography>
              }
            </Box>
            <ChevronRight className="history-card-chevron" />
          </Box>
        ))}

        {questions?.length > 0 &&
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10, 25]}
            count={questions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        }
      </Box>
    );
  }

  return (
    <Box component="div" m={1} className="quiz-history-table-container">
      <Typography variant="h4" className="sub-title">
        History
      </Typography>

      <Paper elevation={3}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SCORE</TableCell>
                <TableCell>NAME</TableCell>
                <TableCell className="hide-in-mobile">DISCIPLINE</TableCell>
                <TableCell className="hide-in-mobile">ORGAN SYSTEM</TableCell>
                <TableCell className="hide-in-mobile">EXAM TYPE</TableCell>
                <TableCell className="hide-in-mobile">TOPICS</TableCell>
                <TableCell className="hide-in-mobile">SUBTOPICS</TableCell>
                <TableCell># Q'S</TableCell>
                <TableCell className="hide-in-mobile">DATE SUBMITTED</TableCell>
                {(!viewOnly || onOversightOpen) && <TableCell></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {questions?.length > 0 && (rowsPerPage > 0 ? questions?.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
                : questions
              )?.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box component="div" className="quiz-percent-progress">
                      <CircularProgress
                        variant="determinate"
                        className="circular-progress-bkg"
                        size={80}
                        thickness={2}
                        value={100}
                      />
                      <CircularProgress
                        variant="determinate"
                        className={`quiz-score-ring quiz-score-ring--${scoreBand(row.percentageCorrect)}`}
                        value={row.percentageCorrect}
                        size={80}
                        thickness={2}
                      />
                      <Box component="div" className="quiz-progress-text">
                        <Box component="div" className="quiz-progress-text-num">
                          {row.percentageCorrect}%
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="button">{row.title}</Typography>
                    {curriculumProvenanceText(row) &&
                      <Typography variant="body2" className="curriculum-provenance">
                        {curriculumProvenanceText(row)}
                      </Typography>
                    }
                    <Box component="div" className="timer-wrapper">
                      <Box
                        component="span"
                        className={row.timerOn ? "blue-text" : ""}
                      >
                        <AccessTime /> Timer <>{row.timerOn ? "ON" : "OFF"}</>
                      </Box>
                      {userRole === 'ROLE_TUTOR' &&
                        <Box
                          component="span"
                          className={row.tutorOn ? "blue-text" : ""}
                        >
                          <PermIdentity /> Tutor <>{row.tutorOn ? "ON" : "OFF"}</>
                        </Box>
                      }
                      {row.assignedByTutorId &&
                        <Box component="span" className="assigned-badge">
                          <PermIdentity /> {viewOnly ? "Assigned by you" : "Assigned by your tutor"}
                        </Box>
                      }
                    </Box>
                  </TableCell>
                  <CategoryCell list={row.category?.discipline} />
                  <CategoryCell list={row.category?.organSystem} />
                  <CategoryCell list={row.category?.examType} />
                  <CategoryCell list={row.medicalCategorizationMap?.topics} />
                  <CategoryCell list={row.medicalCategorizationMap?.subtopics} />
                  <TableCell className="total-ques">{row.total}</TableCell>
                  <TableCell className="hide-in-mobile">{moment(row.complete).format("lll")}</TableCell>
                  {!viewOnly && (
                    <TableCell>
                      <Button
                        data-testid="review-button"
                        variant="outlined"
                        onClick={() => reviewQuestion(row)}
                      >
                        Review
                      </Button>
                    </TableCell>
                  )}
                  {viewOnly && onOversightOpen && (
                    <TableCell>
                      <Button
                        data-testid="oversight-open-button"
                        variant="outlined"
                        onClick={() => onOversightOpen(row)}
                      >
                        Open
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {questions.length === 0 && !isLoading &&
                <tr>
                  <td className="no-data" colSpan={viewOnly ? 9 : 10}>
                    <Box className="qh-empty" role="status">
                      <Typography variant="h6" component="p" className="qh-empty-title">
                        No quizzes yet
                      </Typography>
                      {viewOnly ? (
                        <Typography variant="body2" className="qh-empty-sub">
                          This student hasn&apos;t taken any quizzes yet.
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="body2" className="qh-empty-sub">
                            Create your first quiz to start tracking your progress here.
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            className="qh-empty-cta"
                            onClick={() => history.push("/create-quiz")}
                          >
                            Create a quiz
                          </Button>
                        </>
                      )}
                    </Box>
                  </td>
                </tr>
              }
            </TableBody>
            {(questions?.length > 0) &&
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    count={questions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>
            }
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default QuizHistoryTable;
